
// Map specific face metrics to the 8 axes of the Style Field
// Axis order: Soft(0), Unique(45), Artistic(90), Cool(135), Masculine(180), Practical(225), Classic(270), Cute(315)

export const STYLE_AXES = [
    { id: "soft", label: "Soft", angle: 0 },
    { id: "unique", label: "Unique", angle: 45 },
    { id: "artistic", label: "Artistic", angle: 90 },
    { id: "cool", label: "Cool", angle: 135 },
    { id: "masculine", label: "Masculine", angle: 180 },
    { id: "practical", label: "Practical", angle: 225 },
    { id: "classic", label: "Classic", angle: 270 },
    { id: "cute", label: "Cute", angle: 315 }
];

// Mapping helper for Labels (HIGH, MID, LOW)
const getLabel = (labels, key) => labels && labels[key] ? labels[key] : "MID";

export const calculateStyleField = (faceResult) => {
    if (!faceResult || !faceResult.labels) return null;

    const { labels, masculinity_score } = faceResult;

    // Initial Radius for all axes
    const radii = {
        soft: 50,
        unique: 50,
        artistic: 50,
        cool: 50,
        masculine: 50,
        practical: 50,
        classic: 50,
        cute: 50
    };

    // --- Apply Modifiers based on Prompt ---

    // 1. Classic (270)
    if (getLabel(labels, "Face Aspect Ratio") === "HIGH") radii.classic += 20;
    if (getLabel(labels, "Middle to Cheek Ratio") === "HIGH") radii.classic += 20;
    if (getLabel(labels, "Face Aspect Ratio") === "LOW") radii.classic -= 20;
    if (getLabel(labels, "Middle to Cheek Ratio") === "LOW") radii.classic -= 20;

    // 2. Cute (315)
    if (getLabel(labels, "Face Aspect Ratio") === "LOW") radii.cute += 15;
    if (getLabel(labels, "Middle to Cheek Ratio") === "LOW") radii.cute += 15;
    if (getLabel(labels, "Lower Face Ratio") === "LOW") radii.cute += 15;
    if (getLabel(labels, "Face Aspect Ratio") === "HIGH") radii.cute -= 20;
    if (getLabel(labels, "Middle to Cheek Ratio") === "HIGH") radii.cute -= 20;

    // 3. Masculine (180)
    if (getLabel(labels, "Jaw to Cheek Ratio") === "HIGH") radii.masculine += 15;
    if (getLabel(labels, "Jaw Angle") === "LOW") radii.masculine += 15;
    if (masculinity_score > 60) radii.masculine += 20;
    if (getLabel(labels, "Jaw to Cheek Ratio") === "LOW") radii.masculine -= 20;
    if (getLabel(labels, "Jaw Angle") === "HIGH") radii.masculine -= 20;

    // 4. Soft (0)
    if (getLabel(labels, "Cheek to Forehead Ratio") === "LOW") radii.soft += 15;
    if (getLabel(labels, "Jaw Angle") === "HIGH") radii.soft += 15;
    if (getLabel(labels, "Lower Face Ratio") === "LOW") radii.soft += 15;
    if (getLabel(labels, "Jaw to Cheek Ratio") === "HIGH") radii.soft -= 20;
    if (getLabel(labels, "Jaw Angle") === "LOW") radii.soft -= 20;

    // 5. Unique (45)
    if (getLabel(labels, "Cheek to Forehead Ratio") === "HIGH") radii.unique += 15;
    if (getLabel(labels, "Jaw Angle") === "LOW") radii.unique += 15;
    if (getLabel(labels, "Middle to Cheek Ratio") === "LOW") radii.unique += 15;

    // 6. Artistic (90)
    if (getLabel(labels, "Lower Face Ratio") === "HIGH") radii.artistic += 15;
    if (getLabel(labels, "Face Aspect Ratio") === "HIGH") radii.artistic += 15;
    if (masculinity_score < 40) radii.artistic += 15;
    if (getLabel(labels, "Jaw to Cheek Ratio") === "HIGH") radii.artistic -= 20;

    // 7. Cool (135)
    if (getLabel(labels, "Middle to Cheek Ratio") === "HIGH") radii.cool += 15;
    if (getLabel(labels, "Lower Face Ratio") === "HIGH") radii.cool += 15;
    if (masculinity_score >= 40 && masculinity_score <= 60) radii.cool += 15;
    if (getLabel(labels, "Face Aspect Ratio") === "LOW") radii.cool -= 15;
    if (getLabel(labels, "Cheek to Forehead Ratio") === "HIGH") radii.cool -= 15;

    // 8. Practical (225)
    const midCount = Object.values(labels).filter(v => v === "MID").length;
    if (midCount >= 4) radii.practical += 20;
    else if (midCount <= 2) radii.practical -= 20;

    // --- Clamp Radii ---
    Object.keys(radii).forEach(key => {
        radii[key] = Math.max(20, Math.min(100, radii[key]));
    });

    // --- Derived States ---
    const isSexy = (radii.artistic > 70) && (radii.cool > 60);
    const isHip = (radii.unique > 70) && (radii.cool > 60);

    return { radii, isSexy, isHip };
};

// Calculate SVG Path for Blob
export const getBlobPath = (radii, size = 300) => {
    const center = size / 2;
    const points = STYLE_AXES.map(axis => {
        const r = (radii[axis.id] / 100) * (size / 2 - 20);
        const rad = (axis.angle * Math.PI) / 180;
        return {
            x: center + r * Math.cos(rad),
            y: center + r * Math.sin(rad)
        };
    });

    return catmullRom2bezier(points, true);
};

// Helper to convert points to cubic bezier path
function catmullRom2bezier(points, closed) {
    const size = points.length;
    let d = "";

    for (let i = 0; i < size; i++) {
        const p0 = points[(i - 1 + size) % size];
        const p1 = points[i];
        const p2 = points[(i + 1) % size];
        const p3 = points[(i + 2) % size];

        const tension = 0.2;

        const cp1x = p1.x + (p2.x - p0.x) * tension;
        const cp1y = p1.y + (p2.y - p0.y) * tension;

        const cp2x = p2.x - (p3.x - p1.x) * tension;
        const cp2y = p2.y - (p3.y - p1.y) * tension;

        if (i === 0) d += `M ${p1.x} ${p1.y} `;

        d += `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y} `;
    }

    if (closed) d += "Z";
    return d;
}

export const calculateIdentityDot = (radii) => {
    let sumX = 0;
    let sumY = 0;

    STYLE_AXES.forEach(axis => {
        const r = radii[axis.id] || 50;
        const rad = (axis.angle * Math.PI) / 180;
        sumX += r * Math.cos(rad);
        sumY += r * Math.sin(rad);
    });

    const count = STYLE_AXES.length;
    return {
        r: Math.sqrt(sumX * sumX + sumY * sumY) / count * 3,
        x: sumX / count,
        y: sumY / count
    };
};

export const checkTargetInCloud = (targetCoord, radii) => {
    let tAngle = targetCoord.angle % 360;
    if (tAngle < 0) tAngle += 360;

    const idx = Math.floor(tAngle / 45);
    const axis1 = STYLE_AXES[idx % 8];
    const axis2 = STYLE_AXES[(idx + 1) % 8];

    const r1 = radii[axis1.id];
    const r2 = radii[axis2.id];

    let da = (tAngle - axis1.angle);
    if (da < 0) da += 360;

    const arc = 45;
    const ratio = da / arc;

    const cloudR = r1 + (r2 - r1) * ratio;

    return targetCoord.r <= cloudR;
};

export const checkTargetFeasibility = (targetCoord, radii) => {
    const isInside = checkTargetInCloud(targetCoord, radii);

    // GOAL 방향의 radii 값 확인
    let tAngle = targetCoord.angle % 360;
    if (tAngle < 0) tAngle += 360;
    const idx = Math.floor(tAngle / 45);
    const axis1 = STYLE_AXES[idx % 8];
    const axis2 = STYLE_AXES[(idx + 1) % 8];

    const r1 = radii[axis1.id];
    const r2 = radii[axis2.id];
    let da = (tAngle - axis1.angle);
    if (da < 0) da += 360;
    const arc = 45;
    const ratio = da / arc;
    const targetAxisRadius = r1 + (r2 - r1) * ratio;

    const avgRadius = Object.values(radii).reduce((a, b) => a + b, 0) / 8;

    const isAligned = targetAxisRadius > 60 || targetAxisRadius > avgRadius * 1.1;

    if (isInside) return 'SUCCESS';
    if (isAligned) return 'POTENTIAL';
    return 'CHALLENGE';
};

export const getChugumiCoordinate = (chugumiTitle) => {
    const map = {
        "Cuteness": 315,
        "Softness": 0,
        "Unique": 45,
        "Artistic": 90,
        "Coolness": 135,
        "Masculine": 180,
        "Practical": 225,
        "Classic": 270,
    };

    // Handle Composite Keys (e.g., "Cuteness_Unique")
    if (chugumiTitle && chugumiTitle.includes('_')) {
        const [k1, k2] = chugumiTitle.split('_');
        const a1 = map[k1];
        const a2 = map[k2];

        if (a1 !== undefined && a2 !== undefined) {
            const rad1 = (a1 * Math.PI) / 180;
            const rad2 = (a2 * Math.PI) / 180;

            const x = (Math.cos(rad1) + Math.cos(rad2)) / 2;
            const y = (Math.sin(rad1) + Math.sin(rad2)) / 2;

            let avgAngle = (Math.atan2(y, x) * 180) / Math.PI;
            if (avgAngle < 0) avgAngle += 360;

            return { angle: avgAngle, r: 85 };
        }
    }

    for (const key of Object.keys(map)) {
        if (chugumiTitle && chugumiTitle.includes(key)) {
            return { angle: map[key], r: 85 };
        }
    }

    return { angle: 0, r: 50 };
};

// ===== INTERPRETATION ENGINE =====

const getCloudRadiusAtAngle = (angle, radii) => {
    let tAngle = angle % 360;
    if (tAngle < 0) tAngle += 360;

    const idx = Math.floor(tAngle / 45);
    const axis1 = STYLE_AXES[idx % 8];
    const axis2 = STYLE_AXES[(idx + 1) % 8];

    const r1 = radii[axis1.id];
    const r2 = radii[axis2.id];

    let da = (tAngle - axis1.angle);
    if (da < 0) da += 360;

    const arc = 45;
    const ratio = da / arc;

    return r1 + (r2 - r1) * ratio;
};

export const calculateMatchStatus = (identityDot, targetCoord, radii) => {
    // Convert target to cartesian coordinates
    const maxR = 100;
    const targetRad = (targetCoord.angle * Math.PI) / 180;
    const targetX = (targetCoord.r / maxR) * maxR * Math.cos(targetRad);
    const targetY = (targetCoord.r / maxR) * maxR * Math.sin(targetRad);

    // Calculate distance between ME and GOAL
    const distance = Math.sqrt(
        Math.pow(identityDot.x - targetX, 2) +
        Math.pow(identityDot.y - targetY, 2)
    );

    // Get cloud radius at target direction
    const cloudRadius = getCloudRadiusAtAngle(targetCoord.angle, radii);

    // Calculate sync rate and type
    let syncRate;
    let type;

    if (distance <= cloudRadius * 0.8) {
        type = 'SAFETY';
        syncRate = 90 + Math.floor(Math.random() * 10);
    } else if (distance <= cloudRadius * 1.1) {
        type = 'CHALLENGE';
        syncRate = 70 + Math.floor(Math.random() * 15);
    } else {
        type = 'RISK';
        syncRate = 40 + Math.floor(Math.random() * 20);
    }

    return { type, distance, cloudRadius, syncRate };
};

export const analyzePotential = (radii) => {
    const entries = STYLE_AXES.map(axis => ({
        id: axis.id,
        label: axis.label,
        value: radii[axis.id]
    }));

    entries.sort((a, b) => b.value - a.value);

    const expanded = entries.slice(0, 2);
    const restricted = entries.slice(-2);

    return { expanded, restricted, all: entries };
};

export const generateMatchInsight = (matchStatus, potential) => {
    const { type, syncRate } = matchStatus;
    const { expanded, restricted } = potential;

    const expandedText = expanded.map(e => e.label).join(', ');
    const restrictedText = restricted.map(r => r.label).join(', ');

    return {
        type,
        syncRate,
        expandedText,
        restrictedText,
        emoji: type === 'SAFETY' ? '✨' : type === 'CHALLENGE' ? '🎯' : '🔥'
    };
};

export const generateActionPlan = (matchStatus) => {
    const { type } = matchStatus;

    // Plans will be fetched from content layer using type
    return { type };
};
